import type { LessonLog, MakeupSchedule } from '@/types';
import { formatDate } from '@/lib/datetime';

interface ParentMakeupSectionProps {
  schedules: MakeupSchedule[];
  lessons: LessonLog[];
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function weekdayLabel(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return WEEKDAYS[d.getDay()];
}

export default function ParentMakeupSection({ schedules, lessons }: ParentMakeupSectionProps) {
  const lessonMap: Record<string, LessonLog> = {};
  lessons.forEach((l) => {
    lessonMap[l.id] = l;
  });

  const sorted = [...schedules].sort((a, b) =>
    a.scheduled_date < b.scheduled_date ? 1 : -1,
  );

  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <h2 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
          <i className="ri-calendar-schedule-line"></i>
        </span>
        보충 수업 일정
        <span className="rounded-full bg-background-200 px-2.5 py-0.5 text-[11px] font-semibold text-foreground-600">
          {schedules.length}건
        </span>
      </h2>
      <p className="mt-1.5 text-xs text-foreground-500">
        결석·추가 보충 일정과 보충 수업 진행 여부를 확인할 수 있어요.
      </p>

      {sorted.length === 0 ? (
        <p className="mt-4 rounded-lg border border-dashed border-background-300 py-12 text-center text-sm text-foreground-500">
          아직 예정된 보충 수업이 없어요.
        </p>
      ) : (
        <div className="mt-4 flex flex-col gap-3">
          {sorted.map((s) => {
            const linked = s.lesson_log_id ? lessonMap[s.lesson_log_id] : null;
            const done = Boolean(linked);
            return (
              <article
                key={s.id}
                className="rounded-xl border border-background-200 bg-background-50 p-4 md:p-5"
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-lg ${
                      done ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-700'
                    }`}
                  >
                    <i className={done ? 'ri-check-double-line text-xl' : 'ri-calendar-schedule-line text-xl'}></i>
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="flex items-center gap-1 text-xs text-foreground-500">
                        <i className="ri-calendar-line"></i>
                        {formatDate(s.scheduled_date)}
                        {weekdayLabel(s.scheduled_date) && ` (${weekdayLabel(s.scheduled_date)})`}
                      </span>
                      {done ? (
                        <span className="flex items-center gap-1 rounded-full bg-primary-100 px-2.5 py-0.5 text-xs font-semibold text-primary-800">
                          <i className="ri-check-line"></i>
                          수업 완료
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 rounded-full bg-accent-100 px-2.5 py-0.5 text-xs font-semibold text-accent-800">
                          <i className="ri-time-line"></i>
                          보충 예정
                        </span>
                      )}
                    </div>

                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-600">
                      <span className="flex items-center gap-1">
                        <i className="ri-bookmark-line text-accent-600"></i>
                        {s.reason || '보충'}
                      </span>
                      {s.note && (
                        <span className="flex items-center gap-1">
                          <i className="ri-sticky-note-line text-foreground-400"></i>
                          {s.note}
                        </span>
                      )}
                    </div>

                    {linked?.lesson_scope && (
                      <div className="mt-2.5">
                        <p className="flex items-center gap-1 text-xs font-semibold text-foreground-700">
                          <i className="ri-book-open-line text-primary-600"></i>
                          보충 수업 범위
                        </p>
                        <p className="mt-0.5 whitespace-pre-wrap text-sm leading-relaxed text-foreground-700">
                          {linked.lesson_scope}
                        </p>
                      </div>
                    )}
                    {linked?.next_homework && (
                      <div className="mt-2.5">
                        <p className="flex items-center gap-1 text-xs font-semibold text-foreground-700">
                          <i className="ri-edit-box-line text-secondary-700"></i>
                          다음 숙제
                        </p>
                        <p className="mt-0.5 whitespace-pre-wrap text-sm leading-relaxed text-foreground-700">
                          {linked.next_homework}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}